// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface LeaderboardEntry {
  profile_id: string;
  name: string;
  profile_photo_url: string | null;
  username: string | null;
  count: number;
}

export type LeaderboardFilter = 'trophy' | 'grind' | 'brain' | 'clutch' | 'week';

export const useReactionLeaderboard = (filter: LeaderboardFilter) => {
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      setIsLoading(true);
      
      try {
        // Build the query based on filter
        let query = supabase
          .from('wall_post_reactions')
          .select(`
            post_id,
            reaction_type,
            created_at,
            wall_posts!inner(profile_id)
          `);
        
        // Apply reaction type filter (except for 'week' which gets all)
        if (filter !== 'week') {
          query = query.eq('reaction_type', filter);
        }
        
        // Apply time filter for 'week'
        if (filter === 'week') {
          const weekAgo = new Date();
          weekAgo.setDate(weekAgo.getDate() - 7);
          query = query.gte('created_at', weekAgo.toISOString());
        }
        
        const { data: reactions, error } = await query;
        
        if (error) throw error;
        
        // Aggregate by post author
        const countsByProfile: Record<string, number> = {};
        reactions?.forEach((reaction: any) => {
          const profileId = reaction.wall_posts?.profile_id;
          if (profileId) {
            countsByProfile[profileId] = (countsByProfile[profileId] || 0) + 1;
          }
        });
        
        // Get top 10 profile IDs
        const sortedProfileIds = Object.entries(countsByProfile)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 10);
        
        if (sortedProfileIds.length === 0) {
          setEntries([]);
          setIsLoading(false);
          return;
        }
        
        // Fetch profile details
        const { data: profiles, error: profileError } = await supabase
          .from('profiles')
          .select('id, name, profile_photo_url, username')
          .in('id', sortedProfileIds.map(([id]) => id));
        
        if (profileError) throw profileError;
        
        // Build entries with counts
        const entriesWithCounts: LeaderboardEntry[] = sortedProfileIds.map(([profileId, count]) => {
          const profile = profiles?.find(p => p.id === profileId);
          return {
            profile_id: profileId,
            name: profile?.name || 'Unknown',
            profile_photo_url: profile?.profile_photo_url || null,
            username: profile?.username || null,
            count,
          };
        });
        
        setEntries(entriesWithCounts);
      } catch (err) {
        console.error('Error fetching leaderboard:', err);
        setEntries([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchLeaderboard();
  }, [filter]);
  
  return { entries, isLoading };
};
